<template>
  <div class="view-product-offers">
    <step-title title="Precificação e ofertas" />
    <div class="row my-6">
      <div class="col-12 col-md-6">
        <div
          class="d-flex justify-content-between align-items-center border border-neutral-100 p-5"
        >
          <h3 class="text-neutral-500">
            Preço base
          </h3>
          <div class="d-flex align-items-end">
            <small class="text-neutral-500 mb-1">
              R$
            </small>
            <h3 class="text-neutral-700">
              {{ formatMoney(product.price) }}
            </h3>
          </div>
        </div>
      </div>
      <div class="col-12 col-md-6 mt-4 mt-md-0">
        <div
          class="d-flex justify-content-between align-items-center border border-neutral-100 p-5"
        >
          <h3>Garantia do produto</h3>
          <h3>
            {{ product.warranty }} dia{{ product.warranty == 1 ? "" : "s" }}
          </h3>
        </div>
      </div>
    </div>

    <bp-switch
      class="my-7 my-md-6"
      v-model="smartInstallment"
      label="Ativar recuperação de vendas neste produto"
    />

    <div
      class="row d-none d-md-flex justify-content-between align-items-center mb-4"
    >
      <div class="col-2">
        <div class="d-flex align-items-center">
          <span class="text-neutral-400 fs-14">
            Total: {{ pagination.count }}
          </span>
          <bp-button
            size="md"
            color="neutral"
            variant="glyph"
            @click="dispatch"
          >
            <i class="fa-light fa-refresh" />
          </bp-button>
        </div>
      </div>
      <div class="col-6 col-lg-5">
        <div class="d-flex gap-3">
          <bp-input
            v-model="search"
            size="md"
            placeholder="Buscar..."
            :delay="600"
            @change="dispatch"
          />
          <bp-button
            @click="newOffer"
            size="md"
            label="Nova oferta"
            icon="fa-light fa-plus"
          />
        </div>
      </div>
    </div>

    <div class="d-md-none mb-4">
      <bp-input
        v-model="search"
        class="my-4"
        size="md"
        placeholder="Buscar..."
        :delay="600"
        @change="dispatch"
      />

      <bp-button
        block
        @click="newOffer"
        size="md"
        label="Nova oferta"
        icon="fa-light fa-plus"
      />

      <div class="d-flex align-items-center my-4">
        <span class="text-neutral-400 fs-14">
          Total: {{ pagination.count }}
        </span>
        <bp-button size="md" color="neutral" variant="glyph" @click="dispatch">
          <i class="fa-light fa-refresh" />
        </bp-button>
      </div>
    </div>

    <div v-if="loading" class="text-center">
      <b-spinner variant="primary" />
    </div>

    <div v-else>
      <bp-data-table :columns="columns" :data="offerOptions">
        <template #status="{item,value}">
          <div>
            <bp-tag
              v-if="formatStatusText(value, item)"
              :text="formatStatusText(value, item)"
              :color="formatStatusColor(value, item)"
            />
          </div>
        </template>
      </bp-data-table>
    </div>

    <div class="d-flex justify-content-center justify-content-lg-end my-5">
      <bp-paginator
        v-show="!loading && offers.length"
        v-model="currentPage"
        :pagination="pagination"
        @change-page="dispatch"
      />
    </div>
  </div>
</template>

<script>
import { clearObject, formatMoney } from "@/functions/helpers";
import { defaultOffer } from "@/functions/product";
import ProductActionsMixin from "@/mixins/productActions.js";
import copy from "copy-text-to-clipboard";
import { mapState } from "vuex";

export default {
  mixins: [ProductActionsMixin],
  data() {
    return {
      search: "",
      loading: false,
      currentPage: 1
    };
  },
  computed: {
    ...mapState("product", ["product"]),
    ...mapState("offer", ["offers", "pagination"]),
    offerOptions() {
      return [defaultOffer(this.product), ...this.offers];
    },
    isActive() {
      return this.product.status === "active";
    },
    smartInstallment: {
      get() {
        return this.product.smart_installment;
      },
      set(value) {
        this.$store
          .dispatch("product/put", {
            id: this.$route.params.id,
            smart_installment: value
          })
          .then(() => {
            this.$message.success(
              `Recuperação de vendas ${
                value ? "ativada" : "desativada"
              } com sucesso!`
            );
          })
          .catch(() => {
            this.$message.error("Erro ao atualizar recuperação de vendas.");
          });
      }
    },
    columns() {
      return [
        {
          name: "Nome",
          field: "name",
          size: "1fr"
        },
        {
          name: "Código",
          field: "code",
          size: "0.6fr"
        },
        {
          name: "Real Brasileiro (R$)",
          field: "price",
          transform: v => `R$ ${formatMoney(v)}`,
          size: "1fr"
        },
        {
          name: "Dólar Americano (US$)",
          field: "price_usd",
          transform: v => (parseFloat(v) ? `$ ${formatMoney(v)}` : "-"),
          size: "1fr"
        },
        {
          name: "Euro (€)",
          field: "price_eur",
          transform: v => (parseFloat(v) ? `€ ${formatMoney(v)}` : "-"),
          size: "1fr"
        },
        {
          name: "SCK",
          field: "sck",
          size: "0.6fr"
        },
        {
          name: "Status",
          field: "status",
          size: "0.6fr"
        },
        {
          name: "",
          type: "menu",
          options: this.itemActions
        }
      ];
    }
  },
  methods: {
    formatStatusText(status, item) {
      const statusMap = {
        active: "Ativa",
        inactive: "Inativa"
      };
      return status === "active" && item.is_expired
        ? "Expirada"
        : statusMap[status];
    },
    formatStatusColor(status, item) {
      if (item.is_expired) return "negative";
      const statusMap = {
        active: "positive",
        inactive: "negative"
      };
      return statusMap[status];
    },
    changeOfferStatus(item) {
      this.$store
        .dispatch("offer/put", {
          id: item.id,
          status: item.status === "active" ? "inactive" : "active"
        })
        .then(() => {
          this.$message.success("Status da oferta alterado com sucesso.");
          this.dispatch();
        })
        .catch(() => {
          this.$message.error("Erro ao alterar status da oferta.");
        });
    },
    makeMain(item) {
      this.$store
        .dispatch("product/put", {
          id: this.product.id,
          price: item.price
        })
        .then(() => {
          this.$message.success(
            `Agora o preço base é de R$ ${formatMoney(item.price)}.`
          );
        })
        .catch(() => {
          this.$message.error("Erro ao atualizar oferta principal");
        });
    },
    formatMoney(val) {
      return formatMoney(val);
    },
    newOffer() {
      this.$router.push({
        name: "product-prices-new",
        params: { id: this.$route.params.id }
      });
    },
    itemActions(item) {
      const actions = [];

      actions.push({
        label: "Editar",
        icon: "fa-light fa-pen",
        click: this.edit
      });

      actions.push({
        label: "Copiar link da oferta",
        icon: "fa-light fa-link",
        click: this.copy
      });

      if (item.id !== -1) {
        actions.push({
          label: "Tornar preço base",
          icon: "fa-light fa-brazilian-real-sign",
          dialog: {
            text:
              "Tem certeza que deseja tornar esta oferta como a principal? Após clicar em confirmar, os clientes que acessarem o link de checkout principal do seu produto encontrarão essa oferta.",
            buttonText: "Prosseguir",
            secondaryButtonText: "Cancelar",
            action: this.makeMain,
            icon: "fa-light fa-circle-info"
          }
        });

        actions.push({
          label: "Duplicar oferta",
          icon: "fa-light fa-clone",
          click: this.duplicate
        });

        if (item.id !== -1) {
          actions.push({
            label: `${
              item.status === "active" ? "Desativar" : "Ativar"
            } oferta`,
            class:
              item.status === "active"
                ? "text-negative-600"
                : "text-positive-600",
            icon: "fa-light fa-power-off",
            click: this.changeOfferStatus
          });
        }

        actions.push({
          label: "Excluir oferta",
          icon: "fa-light fa-trash",
          class: "text-negative-600",
          dialog: {
            text:
              "Tem certeza que deseja excluir esta oferta? Seus clientes não poderão mais encontrar e comprar através desta oferta.",
            variant: "negative",
            icon: "fa-light fa-trash",
            secondaryAction: i => this.deleteOffer(i.id),
            buttonText: "Cancelar",
            secondaryButtonText: "Excluir oferta"
          }
        });
      }
      return actions;
    },
    edit(item) {
      if (item.is_main) {
        this.$router.push({ name: "product-prices-main" });
      } else {
        this.$router.push({
          name: "product-prices-edit",
          params: { id: this.$route.params.id, offer_id: item.id }
        });
      }
    },
    copy(item) {
      copy(item.url);
      this.$message.success("Link copiado");
    },
    duplicate(item) {
      this.$store
        .dispatch("offer/duplicate", item)
        .then(() => {
          this.dispatch();
          this.$message.success("Oferta duplicado.");
        })
        .catch(() => this.$message.error("Erro ao duplicar oferta."));
    },
    deleteOffer(id) {
      this.$store
        .dispatch("offer/delete", id)
        .then(() => {
          this.$message.success("Oferta deletada");
          this.dispatch();
        })
        .catch(() => this.$message.error("Erro ao deletar oferta"));
    },
    dispatch(offset = 0) {
      this.loading = true;

      const params = {
        product_id: this.$route.params.id,
        search: this.search,
        offset
      };

      this.$store
        .dispatch("offer/get", clearObject(params))
        .catch(() => {
          this.$message.error("Não foi possível carregar as ofertas");
        })
        .finally(() => {
          this.loading = false;
        });
    }
  },
  mounted() {
    this.dispatch();
  }
};
</script>
